export const THEME_STORAGE_KEY = "k8s-dashboard.theme.v1";
export const THEME_STORAGE_MAX_BYTES = 128;
export const THEMES = ["system", "light", "dark"] as const;
export type ThemePreference = (typeof THEMES)[number];

type ThemeEnvelope = { version: 1; theme: ThemePreference };

const encoder = new TextEncoder();

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function isTheme(value: unknown): value is ThemePreference {
  return typeof value === "string" && THEMES.includes(value as ThemePreference);
}

/** Bootstrap 테스트 corpus와 같은 엄격한 v1 parser입니다. */
export function parseThemePreference(raw: string | null): ThemePreference | undefined {
  if (!raw || raw.length > THEME_STORAGE_MAX_BYTES ||
      encoder.encode(raw).byteLength > THEME_STORAGE_MAX_BYTES) return undefined;
  try {
    const value = JSON.parse(raw) as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) {
      return undefined;
    }
    const record = value as Partial<ThemeEnvelope>;
    const keys = Object.keys(record).sort();
    return keys.length === 2 && keys[0] === "theme" && keys[1] === "version" &&
      record.version === 1 && isTheme(record.theme) ? record.theme : undefined;
  } catch {
    return undefined;
  }
}

/** 누락·접근 거부·손상·크기 초과·알 수 없는 저장값은 모두 System으로 안전하게 수렴합니다. */
export function readThemePreference(): ThemePreference {
  const target = storage();
  if (!target) return "system";
  try {
    const raw = target.getItem(THEME_STORAGE_KEY);
    const parsed = parseThemePreference(raw);
    if (parsed) return parsed;
    if (raw !== null) {
      try {
        target.removeItem(THEME_STORAGE_KEY);
      } catch {
        // 잘못된 저장값은 선호 없음과 같으며 정리는 최선 노력입니다.
      }
    }
  } catch {
    // localStorage 읽기 실패가 shell 렌더링을 막아서는 안 됩니다.
  }
  return "system";
}

export function applyThemePreference(theme: ThemePreference, root = document.documentElement): void {
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

/** 호출자가 메모리 값을 먼저 적용하므로 영속화 실패는 의도적으로 치명적이지 않습니다. */
export function writeThemePreference(theme: ThemePreference): boolean {
  const target = storage();
  if (!target) return false;
  try {
    const raw = JSON.stringify({ version: 1, theme } satisfies ThemeEnvelope);
    if (encoder.encode(raw).byteLength > THEME_STORAGE_MAX_BYTES) return false;
    target.setItem(THEME_STORAGE_KEY, raw);
    return true;
  } catch {
    return false;
  }
}

/** 탭 간 valid/delete/clear/invalid 변경을 실제 최신 저장값으로 수렴하며 invalid는 System입니다. */
export function watchThemePreference(onChange: (theme: ThemePreference) => void): () => void {
  const listener = (event: StorageEvent) => {
    const isClear = event.key === null && event.newValue === null;
    if (event.key !== THEME_STORAGE_KEY && !isClear) return;
    const target = storage();
    if (!target || (event.storageArea !== null && event.storageArea !== target)) return;
    let raw: string | null;
    try {
      raw = target.getItem(THEME_STORAGE_KEY);
    } catch {
      return;
    }
    const next = parseThemePreference(raw) ?? "system";
    applyThemePreference(next);
    onChange(next);
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
}
